import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Users, Award, Clock, Shield, ArrowRight } from "lucide-react";

const AboutPage = () => {
  const values = [
  {
    icon: Award,
    title: "Exceptional Workmanship",
    description: "We deliver high-quality bathroom and kitchen renovations with meticulous planning and attention to detail."
  },
  {
    icon: Users,
    title: "Client Satisfaction",
    description: "We work closely with our clients from initial consultation through completion to ensure expectations are exceeded."
  },
  {
    icon: Clock,
    title: "On Time & On Budget",
    description: "Our experienced team is committed to managing the process efficiently, delivering projects on time and within budget."
  },
  {
    icon: Shield,
    title: "Clear Communication",
    description: "We pride ourselves on clear communication throughout every project, keeping you informed every step of the way."
  }];


  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-[#0f172a]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <p className="text-[#d97706] font-['Barlow_Condensed'] font-semibold uppercase tracking-widest mb-4">
              About Us
            </p>
            <h1 className="font-['Barlow_Condensed'] text-5xl md:text-6xl font-bold text-white uppercase tracking-tight mb-6">
              Building Trust,<br />One Project at a Time
            </h1>
            <p className="text-xl text-slate-400 max-w-2xl">Since 2016, J.N Nickel Contracting has been transforming homes throughout the Fraser Valley with quality craftsmanship and exceptional service.

            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_build-remodel-4/artifacts/dz71v1bt_20260322_191334.jpg"
                alt="Johann Nickel"
                className="w-full aspect-[4/3] object-cover" />

              <div className="absolute -bottom-6 -right-6 bg-[#d97706] text-white p-6 hidden md:block">
                <p className="font-['Barlow_Condensed'] text-4xl font-bold">100+</p>
                <p className="uppercase tracking-wider text-sm">Happy Clients</p>
              </div>
            </div>
            <div>
              <p className="text-[#d97706] font-['Barlow_Condensed'] font-semibold uppercase tracking-widest mb-4">
                Our Story
              </p>
              <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-[#0f172a] uppercase tracking-tight mb-6">
                From Humble Beginnings
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  JN Nickel Contracting Inc. is a dedicated general contracting firm specializing in high-quality bathroom and kitchen renovations. With a strong commitment to delivering exceptional workmanship and ensuring complete client satisfaction, we are excited about the possibility of helping you bring your renovation visions to life.
                </p>
                <p>
                  At JN Nickel Contracting Inc., we pride ourselves on our meticulous planning, attention to detail, and clear communication throughout every project. Our comprehensive services are tailored to meet the unique needs of each client.
                </p>
                <p>
                  We understand that a renovation project can have many moving parts, and our experienced team is committed to managing the process efficiently, on time, and within budget. We work closely with our clients from the initial consultation through completion to ensure their expectations are not just met but exceeded.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 md:py-28 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <p className="text-[#d97706] font-['Barlow_Condensed'] font-semibold uppercase tracking-widest mb-4">
              Our Values
            </p>
            <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-[#0f172a] uppercase tracking-tight">
              What We Stand For
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) =>
            <div
              key={index}
              className="bg-white p-8 border border-slate-100 hover:border-[#d97706]/20 transition-colors"
              data-testid={`value-card-${index}`}>

                <div className="w-14 h-14 bg-[#d97706]/10 flex items-center justify-center mb-6">
                  <value.icon className="w-7 h-7 text-[#d97706]" />
                </div>
                <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-[#0f172a] uppercase mb-3">
                  {value.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {value.description}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Credentials */}
      <section className="py-20 md:py-28 bg-[#f1f5f9]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#d97706] font-['Barlow_Condensed'] font-semibold uppercase tracking-widest mb-4">
                Our Credentials
              </p>
              <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-[#0f172a] uppercase tracking-tight mb-8">
                Licensed & Insured
              </h2>
              <div className="space-y-4">
                {[
                "British Columbia Licensed Contractor",
                "Fully bonded and insured",
                "$2M general liability coverage",
                "Workers' compensation insurance"].
                map((item, index) =>
                <div key={index} className="flex items-start gap-4">
                    <CheckCircle2 className="w-6 h-6 text-[#d97706] flex-shrink-0 mt-0.5" />
                    <span className="text-lg text-slate-700">{item}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="bg-white p-10 border border-slate-200">
              <h3 className="font-['Barlow_Condensed'] text-2xl font-bold text-[#0f172a] uppercase mb-6">
                Our Guarantee
              </h3>
              <p className="text-slate-600 leading-relaxed mb-6">
                We stand behind our work with a comprehensive 1-year warranty on all labor. If anything isn't right, we'll make it right – that's our promise to you.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-[#d97706]" />
                  1-year workmanship warranty
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-[#d97706]" />
                  Manufacturer warranties honored
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-[#d97706]" />
                  Responsive post-project support
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-[#0f172a]">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white uppercase tracking-tight mb-6">
            Let's Work Together
          </h2>
          <p className="text-xl text-slate-400 mb-10 max-w-2xl mx-auto">
            Ready to start your renovation project? Contact us today for a free consultation and estimate.
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-[#d97706] hover:bg-[#b45309] text-white font-['Barlow_Condensed'] font-bold uppercase tracking-wider h-14 px-10 btn-glow"
              data-testid="about-cta-btn">

              Get in Touch
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>);

};

export default AboutPage;